'use client';

/**
 * Product theme provider — portal + analyst (NOT marketing, which keeps its own
 * independent .site-light/.site-dark scope and the 'sig-mkt-theme' key).
 *
 * Selects the theme by writing `data-theme="dark|light"` onto
 * `document.documentElement` (<html>). The dark @theme tokens are the default;
 * the `[data-theme="light"]` scope in globals.css overrides them. Choice
 * persists to localStorage('sig-app-theme'), defaulting to 'dark' (terminal
 * heritage).
 *
 * No flash: the inline FOUC guard in app/layout.tsx applies data-theme on <html>
 * synchronously BEFORE first paint. This provider only reconciles React state to
 * whatever the guard already set, then writes future changes — so there's no
 * hydration mismatch and no flash of the wrong theme.
 *
 * Bespoke (no next-themes dep) to match the lean house style + the existing
 * marketing toggle pattern.
 */

import { createContext, useCallback, useContext, useEffect, useState } from 'react';

const STORAGE_KEY = 'sig-app-theme';

export type Theme = 'dark' | 'light';

interface ThemeContextValue {
  theme: Theme;
  toggle: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStoredTheme(): Theme {
  if (typeof document !== 'undefined') {
    // The FOUC guard already resolved + applied the theme to <html>; trust it
    // as the source of truth so SSR markup and first client render agree.
    const applied = document.documentElement.getAttribute('data-theme');
    if (applied === 'light' || applied === 'dark') return applied;
  }
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    /* private browsing / blocked storage — fall through to default */
  }
  return 'dark';
}

function applyTheme(theme: Theme) {
  document.documentElement.setAttribute('data-theme', theme);
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* private browsing — the in-session flip still works */
  }
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Start at the default so SSR + first client render match; the effect below
  // reconciles to whatever the inline guard applied, before any paint diff.
  const [theme, setThemeState] = useState<Theme>('dark');

  useEffect(() => {
    setThemeState(readStoredTheme());
  }, []);

  const setTheme = useCallback((next: Theme) => {
    applyTheme(next);
    setThemeState(next);
  }, []);

  const toggle = useCallback(() => {
    setThemeState((prev) => {
      const next: Theme = prev === 'dark' ? 'light' : 'dark';
      applyTheme(next);
      return next;
    });
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, toggle, setTheme }}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return ctx;
}
